<script setup>
import { ref } from 'vue';
import ChatIcon from './ChatIcon.vue';

const props = defineProps({
  message: { type: Object, required: true },
  t: { type: Function, required: true },
});

const emit = defineEmits(['choose']);
const chosen = ref(null);

// A short beat on the picked row before it leaves, so the choice reads as confirmed.
const choose = (option, index) => {
  if (chosen.value !== null) return;
  chosen.value = index;
  setTimeout(() => emit('choose', props.message, option), 160);
};

defineExpose({
  chooseByNumber: number => {
    const option = props.message.items[number - 1];
    if (option) choose(option, number - 1);
  },
});
</script>

<template>
  <div
    class="shrink-0 border-t border-line bg-surface px-3 pb-1 pt-2.5 animate-tray-in motion-reduce:animate-none"
    role="group"
    :aria-label="t('CHOICES_LABEL')"
  >
    <p
      class="px-1 pb-2 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-muted"
    >
      {{ t('CHOICES_LABEL') }}
    </p>
    <ul
      class="grid max-h-[40vh] gap-1.5 overflow-y-auto overscroll-contain pb-2 sm:max-h-[248px]"
    >
      <li v-for="(option, index) in message.items" :key="option.value">
        <button
          type="button"
          class="group flex w-full items-center gap-3 rounded-ark border px-3 py-2.5 text-left text-[14px] font-medium leading-snug outline-none transition duration-150 focus-visible:ring-2 focus-visible:ring-primary/40 motion-reduce:transition-none"
          :class="
            chosen === index
              ? 'scale-[0.99] border-primary bg-primary text-on-primary'
              : 'border-line bg-canvas text-ink hover:border-primary/45 hover:bg-primary/[0.06]'
          "
          :disabled="chosen !== null"
          @click="choose(option, index)"
        >
          <kbd
            v-if="index < 9"
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border font-sans text-[11px] font-semibold [@media(hover:none)]:hidden"
            :class="
              chosen === index
                ? 'border-on-primary/30 text-on-primary'
                : 'border-line bg-surface text-muted group-hover:text-primary'
            "
          >
            {{ index + 1 }}
          </kbd>
          <span class="min-w-0 flex-1">{{ option.title }}</span>
          <ChatIcon
            name="chevron-right"
            class="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
            :class="
              chosen === index
                ? 'text-on-primary'
                : 'text-muted group-hover:text-primary'
            "
          />
        </button>
      </li>
    </ul>
  </div>
</template>
